import React, { useState, useEffect } from 'react';
import { Zap, Check, Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import AuthModal from '@/components/AuthModal';
import { PRICING_CONFIG } from '@/config/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small inline row shown below the balance number. Reads user.topup_expires_at
 * (set on any top-up grant, reset on every new purchase) and displays a
 * friendly "expires on Jul 5, 2027" line. Amber when <30 days remain.
 * Renders NOTHING if the user has no top-up credits.
 */
function TopupExpiryLine({ user }) {
  const topup = Number(user?.topup_credits || 0);
  const expiresAt = user?.topup_expires_at;
  if (topup <= 0 || !expiresAt) return null;

  const expiryDate = new Date(expiresAt);
  if (Number.isNaN(expiryDate.getTime())) return null;

  const now = Date.now();
  const msLeft = expiryDate.getTime() - now;
  const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
  const soon = daysLeft <= 30;
  const label = expiryDate.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  return (
    <p
      className={`text-xs mt-4 ${soon ? 'text-[#FFB238]' : 'text-gray-500'}`}
      data-testid="topup-expiry-line"
    >
      {daysLeft >= 0
        ? <>Top-up credits expire on <strong>{label}</strong>{soon ? ` (${daysLeft} day${daysLeft === 1 ? '' : 's'} left)` : ''}.</>
        : <>Top-up credits expired on {label}.</>
      }
    </p>
  );
}

function Credits() {
  const { user, token, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingTopup, setLoadingTopup] = useState(null);
  const [loadingDowngrade, setLoadingDowngrade] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentProcessed = React.useRef(false);

  useEffect(() => {
    if (sessionId && user && !paymentProcessed.current) {
      paymentProcessed.current = true;
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, user]);

  const checkPaymentStatus = async (sid) => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.info('Payment verification in progress. Your credits will be added shortly.');
        window.history.replaceState({}, '', '/credits');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.status === 'completed') {
          toast.success(`Payment successful! ${response.data.credits_added} credits added.`);
          await fetchUser();
          window.history.replaceState({}, '', '/credits');
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Payment status check failed:', error);
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  const plans = PRICING_CONFIG.plans;
  const topups = PRICING_CONFIG.topups;

  const handlePurchase = async (planId) => {
    if (!user) { setShowAuthModal(true); return; }
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        null,
        {
          params: { plan: planId, origin_url: originUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const handleTopup = async (packId) => {
    if (!user) { setShowAuthModal(true); return; }
    setLoadingTopup(packId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-topup`,
        null,
        {
          params: { pack: packId, origin_url: originUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoadingTopup(null);
    }
  };

  const handleDowngrade = async (planId) => {
    if (!user) return;
    setLoadingDowngrade(planId);
    try {
      const response = await axios.post(
        `${API}/payments/downgrade`,
        null,
        {
          params: { plan: planId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(response.data.message);
      await fetchUser();
      setLoadingDowngrade(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process downgrade');
      setLoadingDowngrade(null);
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => { setShowAuthModal(false); window.location.reload(); }}
      />

      {/* Header */}
      <div className="text-center mb-12" data-testid="credits-header">
        <div className="w-16 h-16 rounded-2xl bg-[#1A2035] border border-[#222D4A] flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-[#FFB238]" fill="#FFB238" />
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Your Credits</h1>
        <p className="text-gray-300">Purchase a monthly plan or grab a one-time top-up to fuel your creations.</p>
        <p className="text-gray-500 text-sm mt-2">Cancel anytime — no contracts, no commitments.</p>
      </div>

      {/* Current Balance */}
      {user && (
        <div className="max-w-md mx-auto mb-12">
          <div className="bg-gradient-to-br from-[#1A2035] to-[#222D4A] rounded-2xl p-8 border border-[#2A3550] text-center" data-testid="credit-balance-card">
            <div className="w-16 h-16 rounded-2xl bg-[#FFB238] flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-[#0F1419]" fill="#0F1419" />
            </div>
            <p className="text-gray-400 text-sm mb-2">Available Credits</p>
            <p className="text-6xl font-bold text-white mb-2" data-testid="credit-balance">{user?.credits_balance || 0}</p>
            <p className="text-gray-400 text-sm">credits remaining</p>
            <TopupExpiryLine user={user} />
          </div>
        </div>
      )}

      {/* Low Credits Warning */}
      {user && user.credits_balance < 150 && (
        <div className="bg-green-500 rounded-xl p-4 mb-8 max-w-5xl mx-auto" data-testid="low-credits-warning">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-white flex-shrink-0" />
            <p className="text-white font-medium">You're running low on credits. Subscribe to a plan or grab a top-up below.</p>
          </div>
        </div>
      )}

      {/* Top-Up Section — PRIMARY (one-time, 1-year expiry) */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-white text-center mb-3">Credit Top-Ups</h2>
        <p className="text-gray-400 text-center mb-10">
          One-time purchase. Credits expire 1 year after purchase. All packs include the same perks.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-testid="topup-plans">
          {topups.map((pack) => (
            <div
              key={pack.id}
              className={`relative bg-[#1A2035] rounded-2xl p-8 border transition-all flex flex-col ${
                pack.popular
                  ? 'border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                  : 'border-[#222D4A] hover:border-[#FFB238]/50'
              }`}
              data-testid={`topup-card-${pack.id}`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FFB238] text-[#0F1419] text-xs font-bold" data-testid={`topup-popular-badge-${pack.id}`}>
                  Most Popular
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{pack.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{pack.tagline}</p>
              </div>
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-bold ${pack.popular ? 'text-[#FFB238]' : 'text-white'}`}>${pack.price}</span>
                  <span className="text-gray-400 text-sm">one-time</span>
                </div>
                {pack.bonus && (
                  <span className="inline-block mt-2 px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold" data-testid={`topup-bonus-${pack.id}`}>
                    {pack.bonus}
                  </span>
                )}
              </div>
              <div className="space-y-3 mb-8 flex-1">
                {pack.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleTopup(pack.id)}
                disabled={loadingTopup !== null}
                className={`w-full px-6 py-3.5 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-auto ${
                  pack.popular
                    ? 'bg-[#FFB238] text-[#0F1419] hover:bg-[#FFDA82]'
                    : 'bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550]'
                }`}
                data-testid={`topup-button-${pack.id}`}
              >
                {loadingTopup === pack.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                  </span>
                ) : (
                  user ? 'Buy Now' : 'Sign Up to Purchase'
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* OR divider */}
      <div className="max-w-5xl mx-auto mb-12 flex items-center gap-4" data-testid="or-divider">
        <div className="flex-1 h-px bg-[#222D4A]"></div>
        <span className="px-4 py-1 rounded-full border border-[#222D4A] bg-[#1A2035] text-gray-400 text-sm font-semibold uppercase tracking-wider">Or</span>
        <div className="flex-1 h-px bg-[#222D4A]"></div>
      </div>

      {/* Monthly Plans Section — SECONDARY */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-white text-center mb-3">Monthly Plans</h2>
        <p className="text-gray-400 text-center mb-10">Fresh credits every month. All plans include a Commercial Usage License.</p>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="pricing-plans">
          {plans.map((plan) => {
            const tierOrder = { free: 0, starter: 1, plus: 2, pro: 3 };
            const userTier = user?.subscription_tier || 'free';
            const isActive = userTier === plan.id;
            const isUpgrade = tierOrder[plan.id] > tierOrder[userTier];
            const isDowngrade = tierOrder[plan.id] < tierOrder[userTier] && userTier !== 'free';
            const isPendingDowngrade = user?.pending_downgrade_to === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative bg-[#1A2035] rounded-2xl p-8 border transition-all flex flex-col ${
                  isActive
                    ? 'border-green-500 shadow-lg shadow-green-500/10'
                    : plan.popular && !isDowngrade
                      ? 'border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                      : 'border-[#222D4A] hover:border-[#FFB238]/50'
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {isActive && !user?.pending_downgrade_to && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-green-500 text-white text-xs font-bold" data-testid="current-plan-badge">
                    Current Plan
                  </div>
                )}
                {isActive && user?.pending_downgrade_to && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-white text-xs font-bold" data-testid="downgrading-badge">
                    Downgrading Soon
                  </div>
                )}
                {isPendingDowngrade && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-blue-500 text-white text-xs font-bold" data-testid="switching-to-badge">
                    Switching To
                  </div>
                )}
                {!isActive && plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FFB238] text-[#0F1419] text-xs font-bold" data-testid="most-popular-badge">
                    Most Popular
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{plan.tagline}</p>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${isActive ? 'text-green-400' : plan.popular ? 'text-[#FFB238]' : 'text-white'}`}>${plan.price}</span>
                    <span className="text-gray-400 text-sm">/ {plan.period}</span>
                  </div>
                  {plan.bonus && (
                    <span className="inline-block mt-2 px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold">{plan.bonus}</span>
                  )}
                </div>
                <div className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                {isActive ? (
                  <button
                    onClick={() => navigate('/account')}
                    className="w-full px-6 py-3.5 rounded-lg font-bold bg-green-500/15 text-green-400 border border-green-500/30 cursor-pointer hover:bg-green-500/25 transition-all"
                    data-testid={`manage-button-${plan.id}`}
                  >
                    Manage Subscription
                  </button>
                ) : isDowngrade ? (
                  <button
                    onClick={() => handleDowngrade(plan.id)}
                    disabled={loadingDowngrade !== null || isPendingDowngrade}
                    className={`w-full px-6 py-3.5 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      isPendingDowngrade
                        ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                        : 'bg-[#222D4A] text-gray-400 border border-[#2A3550] hover:bg-[#2A3550]'
                    }`}
                    data-testid={`downgrade-button-${plan.id}`}
                  >
                    {loadingDowngrade === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                      </span>
                    ) : isPendingDowngrade ? (
                      'Downgrade Scheduled'
                    ) : (
                      'Downgrade'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePurchase(plan.id)}
                    disabled={loading}
                    className={`w-full px-6 py-3.5 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      plan.popular
                        ? 'bg-[#FFB238] text-[#0F1419] hover:bg-[#FFDA82]'
                        : 'bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550]'
                    }`}
                    data-testid={`purchase-button-${plan.id}`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                      </span>
                    ) : (
                      user ? (isUpgrade ? 'Upgrade' : 'Subscribe Now') : 'Sign Up to Subscribe'
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-gray-400 text-sm mb-2">
          Secure payments powered by Stripe. Monthly credits reset each billing cycle. Top-Up packs are valid for 1 year from your most recent purchase.
        </p>
        <p className="text-gray-500 text-xs">
          By purchasing, you agree to our <Link to="/terms" className="text-gray-400 hover:text-white underline">Terms and Conditions</Link> and <Link to="/refund-policy" className="text-gray-400 hover:text-white underline">Refund Policy</Link>.
        </p>
      </div>
    </div>
  );
}

export default Credits;
